import React, { useState, useMemo, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import {
    BarChart3, MapPin, Users, LogOut, Menu, Search, Plus, Server,
    FileText, CheckCircle2, Clock, Inbox, Activity, Siren, ChevronRight,
    ChevronLeft, Send, Paperclip, Trash2, Settings, Filter, AlertCircle,
    User as UserIcon, Bell, PieChart as PieChartIcon, BookOpen
} from 'lucide-react';

// Import types
import { User, Site, Ticket, Status, TimelineEvent } from './types';

// Import services
import { StorageService } from './services/storage.service';
import { MOCK_SITES_DATA, MOCK_USERS_DATA, INITIAL_TICKETS } from './services/mockData';

// Import utilities
import {
    getPriorityColor, getStatusColor, getStatusLabel, getRelativeTime, getSLAStatus, getSLAColor, formatSLATime,
    calculatePriority
} from './utils/helpers';

// Import components
import { AdidasLogo, KPICard, Button, EmptyState } from './components/shared';
import { LoginScreen } from './components/auth/LoginScreen';
import { NotificationContainer, useNotifications } from './components/shared/Notifications';
import { CreateTicketWizard } from './components/tickets/CreateTicketWizard';
import { StoreTicketGrid } from './components/dashboard/StoreTicketGrid';
import { AssetsInventory } from './components/assets/AssetsInventory';
import { SiteManagement } from './components/admin/SiteManagement';
import { UserManagement } from './components/admin/UserManagement';
import { TicketDetail } from './components/tickets/TicketDetail';
import { ExportMenu } from './components/reports/ExportMenu';
import { AnalyticsDashboard } from './components/dashboard/AnalyticsDashboard';
import { KnowledgeBase } from './components/knowledge/KnowledgeBase';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useSupabaseData } from './hooks/useSupabaseData';
import { AuthUserService } from './services/auth-user.service';
import { useAuth } from './hooks/useAuth';
import { useTicketOperations } from './hooks/useTicketOperations';
import { useRealtimeSubscriptions } from './hooks/useRealtimeSubscriptions';
import { supabase } from './config/supabase';
import { AppRouter } from './AppRouter';


// Main App Component
const App = () => {
    // Custom hooks for clean separation of concerns
    const { currentUser, login, logout } = useAuth();
    const { notifications, history: notifHistory, unreadCount, addNotification, removeNotification, markAllRead, clearHistory } = useNotifications();
    const supabaseData = useSupabaseData();

    // State management
    const [sites, setSites] = useState<Site[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [view, setView] = useState<'DASHBOARD' | 'TICKET_DETAIL' | 'SITES' | 'USERS' | 'ASSETS' | 'HISTORY' | 'ANALYTICS' | 'KNOWLEDGE'>('DASHBOARD');
    const [dashboardMode, setDashboardMode] = useState<'KPI' | 'STORES'>('KPI');
    const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [showWizard, setShowWizard] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);

    // Ticket operations hook
    const ticketOps = useTicketOperations({
        currentUser,
        users,
        onNotify: addNotification,
        onReload: async () => {
            if (currentUser) {
                await supabaseData.loadTickets(currentUser.id, currentUser.role);
            }
        }
    });

    // Realtime subscriptions
    useRealtimeSubscriptions({
        currentUser,
        onTicketChange: () => {
            if (currentUser) {
                supabaseData.loadTickets(currentUser.id, currentUser.role);
            }
        },
        onNotify: addNotification
    });

    // Load initial data from Supabase
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                await supabaseData.loadSites();
                await supabaseData.loadUsers();
            } catch (err) {
                console.error('Error loading initial data:', err);
                const storedSites = StorageService.loadSites();
                const storedUsers = StorageService.loadUsers();
                if (storedSites.length > 0) setSites(storedSites);
                if (storedUsers.length > 0) setUsers(storedUsers);
            }
        };
        loadInitialData();
    }, []);

    // Sync Supabase data to local state
    useEffect(() => {
        if (supabaseData.sites.length > 0) setSites(supabaseData.sites);
    }, [supabaseData.sites]);

    useEffect(() => {
        if (supabaseData.users.length > 0) setUsers(supabaseData.users);
    }, [supabaseData.users]);

    useEffect(() => {
        if (supabaseData.tickets.length > 0) setTickets(supabaseData.tickets);
    }, [supabaseData.tickets]);

    // Load tickets when user logs in
    useEffect(() => {
        if (currentUser) {
            supabaseData.loadTickets(currentUser.id, currentUser.role);
        }
    }, [currentUser]);

    const handleLogin = (user: User) => {
        login(user);
        addNotification('success', `Bienvenido, ${user.name}!`);
    };

    const handleLogout = () => {
        logout();
        setView('DASHBOARD');
        setSelectedTicket(null);
        addNotification('info', 'Sesión cerrada correctamente');
    };

    const handleDeleteTicket = async (ticketId: string) => {
        const success = await ticketOps.deleteTicket(ticketId);
        if (success && view === 'TICKET_DETAIL' && selectedTicket?.id === ticketId) {
            setView('DASHBOARD');
            setSelectedTicket(null);
        }
    };

    const handleArchiveTicket = async (ticketId: string) => {
        const success = await ticketOps.archiveTicket(ticketId);
        if (success && view === 'TICKET_DETAIL' && selectedTicket?.id === ticketId) {
            setView('DASHBOARD');
            setSelectedTicket(null);
        }
    };

    const handleUpdateTicket = async (updatedTicket: Ticket) => {
        const success = await ticketOps.updateTicket(updatedTicket);
        if (success) {
            setSelectedTicket(updatedTicket);
        }
    };

    const handleCreateTicket = async (data: any) => {
        const success = await ticketOps.createTicket(data);
        if (success) {
            setShowWizard(false);
        }
    };

    // ============================================
    // USER MANAGEMENT HANDLERS (RPC CALLS)
    // ============================================
    const handleUpdateUser = async (userUpdate: User & { password?: string }) => {
        try {
            console.log('[index.tsx] handleUpdateUser called', userUpdate.id);
            const result = await AuthUserService.updateUser(userUpdate.id, userUpdate);

            if (result.success) {
                // Reload users from Supabase
                await supabaseData.loadUsers();
                addNotification('success', 'Usuario actualizado correctamente');
            } else {
                addNotification('error', result.error || 'Error al actualizar usuario');
            }
        } catch (err: any) {
            console.error('[index.tsx] Error in handleUpdateUser:', err);
            addNotification('error', err.message || 'Error al actualizar usuario');
        }
    };

    const handleCreateUser = async (newUser: Omit<User, 'id'> & { password: string }) => {
        try {
            console.log('[index.tsx] handleCreateUser called');
            const result = await AuthUserService.createUser(newUser);

            if (result.success) {
                // Reload users from Supabase
                await supabaseData.loadUsers();
                addNotification('success', 'Usuario creado correctamente');
            } else {
                addNotification('error', result.error || 'Error al crear usuario');
            }
        } catch (err: any) {
            console.error('[index.tsx] Error in handleCreateUser:', err);
            addNotification('error', err.message || 'Error al crear usuario');
        }
    };

    const handleDeleteUser = async (userId: string) => {
        try {
            console.log('[index.tsx] handleDeleteUser called', userId);
            const result = await AuthUserService.deleteUser(userId);

            if (result.success) {
                // Reload users from Supabase
                await supabaseData.loadUsers();
                addNotification('success', 'Usuario eliminado correctamente');
            } else {
                addNotification('error', result.error || 'Error al eliminar usuario');
            }
        } catch (err: any) {
            console.error('[index.tsx] Error in handleDeleteUser:', err);
            addNotification('error', err.message || 'Error al eliminar usuario');
        }
    };
    const handleUpdateSite = async (updatedSite: Site) => {
        try {
            // 1. Update Site details in 'sites' table
            const { error } = await supabase
                .from('sites')
                .update({
                    contact: updatedSite.contact,
                    phone: updatedSite.phone,
                    email: updatedSite.email,
                    address: updatedSite.address,
                })
                .eq('id', updatedSite.id);

            if (error) throw error;

            // 2. Handle Password Update (if provided)
            if (updatedSite.password && updatedSite.password.trim().length > 0) {
                console.log('[handleUpdateSite] Password change requested for site:', updatedSite.name);

                // Find the associated STORE user
                const storeUser = users.find(u => u.siteId === updatedSite.id && u.role === 'STORE');

                if (storeUser) {
                    console.log('[handleUpdateSite] Found store user:', storeUser.email);

                    // Call AuthUserService to update password via RPC
                    const authResult = await AuthUserService.updateUser(storeUser.id, {
                        ...storeUser,
                        password: updatedSite.password
                    });

                    if (!authResult.success) {
                        console.error('[handleUpdateSite] Failed to update password:', authResult.error);
                        addNotification('warning', 'Datos de tienda actualizados, pero error al actualizar contraseña: ' + authResult.error);
                    } else {
                        addNotification('success', `Tienda ${updatedSite.name} y contraseña actualizadas correctamente`);
                    }
                } else {
                    console.warn('[handleUpdateSite] No STORE user found for site:', updatedSite.name);
                    addNotification('warning', 'Datos guardados, pero no se encontró usuario de tienda para actualizar contraseña');
                }
            } else {
                addNotification('success', `Tienda ${updatedSite.name} actualizada correctamente`);
            }

            // Update local state
            setSites(prev => prev.map(s => s.id === updatedSite.id ? updatedSite : s));

        } catch (error: any) {
            console.error('Error updating site:', error);
            addNotification('error', 'Error al actualizar tienda: ' + error.message);
        }
    };

    const getVisibleTickets = useMemo(() => {
        if (!currentUser) return [];
        let filtered = currentUser.role === 'STORE'
            ? tickets.filter(t => t.siteId === currentUser.siteId)
            : tickets;

        if (searchQuery) {
            filtered = filtered.filter(t =>
                t.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                t.siteName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                t.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
                t.description.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        return filtered;
    }, [currentUser, tickets, searchQuery]);

    if (!currentUser) {
        return <LoginScreen sites={sites} users={users} onLogin={handleLogin} />;
    }

    const visibleTickets = getVisibleTickets;
    const isStore = currentUser.role === 'STORE';
    const isAdmin = currentUser.role === 'HELPDESK' || currentUser.role === 'COORD';

    const triageTickets = visibleTickets.filter(t => t.status === 'OPEN');

    const activeTickets = visibleTickets.filter(t =>
        t.status === 'IN_PROGRESS' ||
        t.status === 'WAITING_INFO' ||
        t.status === 'IN_TRANSIT' ||
        t.status === 'ON_SITE' ||
        t.status === 'WAITING_PARTS'
    );
    const historyTickets = visibleTickets.filter(t => t.status === 'RESOLVED' || t.status === 'CLOSED');
    const criticalTickets = visibleTickets.filter(t => t.priority === 'P1' && t.status !== 'CLOSED');

    return (
        <>
            <NotificationContainer notifications={notifications} onDismiss={removeNotification} />

            <div className="flex h-screen bg-gray-50 font-sans text-gray-900">
                {/* Sidebar */}
                <div className="w-64 bg-black text-white flex flex-col hidden md:flex">
                    <div className="p-8 border-b border-gray-800 flex flex-col items-center justify-center">
                        <AdidasLogo className="w-12 h-12 text-white mb-3" />
                        <span className="text-2xl font-bold tracking-tighter uppercase">adidas</span>
                        <span className="text-[10px] text-gray-500 tracking-widest uppercase mt-1">Portal Redes</span>
                    </div>

                    <nav className="flex-1 p-4 space-y-1">
                        <button onClick={() => setView('DASHBOARD')} className={`w-full flex items-center p-3 rounded-lg hover:bg-gray-800 transition ${view === 'DASHBOARD' ? 'bg-gray-800 text-white' : 'text-gray-400'}`}>
                            <BarChart3 size={20} className="mr-3" /> Dashboard
                        </button>
                        <button onClick={() => setView('HISTORY')} className={`w-full flex items-center p-3 rounded-lg hover:bg-gray-800 transition ${view === 'HISTORY' ? 'bg-gray-800 text-white' : 'text-gray-400'}`}>
                            <Clock size={20} className="mr-3" /> Historial
                        </button>

                        {!isStore && (
                            <>
                                {isAdmin && (
                                    <button onClick={() => setView('ANALYTICS')} className={`w-full flex items-center p-3 rounded-lg hover:bg-gray-800 transition ${view === 'ANALYTICS' ? 'bg-gray-800 text-white' : 'text-gray-400'}`}>
                                        <PieChartIcon size={20} className="mr-3" /> Métricas
                                    </button>
                                )}
                                <button onClick={() => setView('ASSETS')} className={`w-full flex items-center p-3 rounded-lg hover:bg-gray-800 transition ${view === 'ASSETS' ? 'bg-gray-800 text-white' : 'text-gray-400'}`}>
                                    <Server size={20} className="mr-3" /> Activos
                                </button>
                                <button onClick={() => setView('SITES')} className={`w-full flex items-center p-3 rounded-lg hover:bg-gray-800 transition ${view === 'SITES' ? 'bg-gray-800 text-white' : 'text-gray-400'}`}>
                                    <MapPin size={20} className="mr-3" /> Red de Tiendas
                                </button>
                                {isAdmin && (
                                    <button onClick={() => setView('USERS')} className={`w-full flex items-center p-3 rounded-lg hover:bg-gray-800 transition ${view === 'USERS' ? 'bg-gray-800 text-white' : 'text-gray-400'}`}>
                                        <Users size={20} className="mr-3" /> Usuarios
                                    </button>
                                )}
                            </>
                        )}

                        <div className="pt-4 mt-2 border-t border-gray-800">
                            <button onClick={() => setView('KNOWLEDGE')} className={`w-full flex items-center p-3 rounded-lg hover:bg-gray-800 transition ${view === 'KNOWLEDGE' ? 'bg-gray-800 text-white' : 'text-gray-400'}`}>
                                <BookOpen size={20} className="mr-3" /> Base de Conocimiento
                            </button>
                        </div>
                    </nav>

                    <div className="p-4 border-t border-gray-800">
                        <div className="flex items-center mb-3">
                            <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center font-bold text-sm mr-3">{currentUser.avatar}</div>
                            <div>
                                <p className="text-sm font-bold truncate w-32">{currentUser.name}</p>
                                <p className="text-xs text-gray-500 capitalize">{currentUser.siteName || 'Mesa Central'}</p>
                            </div>
                        </div>
                        <button onClick={handleLogout} className="flex items-center text-xs text-gray-400 hover:text-white transition">
                            <LogOut size={14} className="mr-1" /> Cerrar Sesión
                        </button>
                    </div>
                </div>

                {/* Main Content */}
                <div className="flex-1 flex flex-col overflow-hidden">
                    {/* Top bar */}
                    <div className="bg-white border-b px-6 py-3 flex justify-between items-center">
                        <div className="relative w-96 hidden md:block">
                            <Search size={18} className="absolute left-3 top-2.5 text-gray-400" />
                            <input
                                type="text"
                                placeholder={isStore ? "Buscar mis tickets..." : "Buscar global por ID, tienda..."}
                                className="w-full pl-10 pr-4 py-2 bg-gray-100 border-none rounded-lg text-sm focus:ring-2 focus:ring-black"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        <div className="flex space-x-3">
                            <button onClick={() => setShowWizard(true)} className="px-4 py-2 bg-black text-white text-sm font-bold rounded-lg hover:bg-gray-800 flex items-center shadow-lg transition">
                                <Plus size={18} className="mr-2" /> Crear Solicitud
                            </button>

                            <div className="relative">
                                <button
                                    onClick={() => { setShowNotifications(!showNotifications); markAllRead(); }}
                                    className="p-2 text-gray-400 hover:text-black hover:bg-gray-100 rounded-full transition relative"
                                >
                                    <Bell size={20} />
                                    {unreadCount > 0 && (
                                        <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white"></span>
                                    )}
                                </button>

                                {showNotifications && (
                                    <>
                                        <div className="fixed inset-0 z-10" onClick={() => setShowNotifications(false)} />
                                        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border z-20 overflow-hidden">
                                            <div className="p-3 border-b flex justify-between items-center bg-gray-50">
                                                <h3 className="font-bold text-sm">Notificaciones</h3>
                                                {notifHistory.length > 0 && (
                                                    <button onClick={clearHistory} className="text-xs text-blue-600 hover:underline">
                                                        Borrar todo
                                                    </button>
                                                )}
                                            </div>
                                            <div className="max-h-80 overflow-y-auto">
                                                {notifHistory.length === 0 ? (
                                                    <div className="p-8 text-center text-gray-400 text-sm">
                                                        <Bell size={32} className="mx-auto mb-2 opacity-50" />
                                                        No hay notificaciones
                                                    </div>
                                                ) : (
                                                    <div className="divide-y">
                                                        {notifHistory.map(n => (
                                                            <div key={n.id} className="p-3 hover:bg-gray-50 flex items-start space-x-3">
                                                                <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${n.type === 'success' ? 'bg-green-500' :
                                                                    n.type === 'error' ? 'bg-red-500' :
                                                                        'bg-blue-500'
                                                                    }`} />
                                                                <div>
                                                                    <p className="text-sm text-gray-800 leading-snug">{n.message}</p>
                                                                    <p className="text-xs text-gray-400 mt-1">{new Date(n.timestamp).toLocaleString()}</p>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Content Area */}
                    <div className="flex-1 overflow-y-auto p-6">
                        {view === 'DASHBOARD' && !isStore && (
                            <div className="space-y-8">

                                <div className="flex justify-between items-center">
                                    <div className="flex items-center bg-gray-100 p-1 rounded-lg w-fit">
                                        <button
                                            onClick={() => setDashboardMode('KPI')}
                                            className={`px-4 py-2 text-sm font-bold rounded-md transition ${dashboardMode === 'KPI' ? 'bg-white shadow text-black' : 'text-gray-500 hover:text-gray-800'}`}
                                        >
                                            <div className="flex items-center">
                                                <BarChart3 size={16} className="mr-2" />
                                                Vista General
                                            </div>
                                        </button>
                                        <button
                                            onClick={() => setDashboardMode('STORES')}
                                            className={`px-4 py-2 text-sm font-bold rounded-md transition ${dashboardMode === 'STORES' ? 'bg-white shadow text-black' : 'text-gray-500 hover:text-gray-800'}`}
                                        >
                                            <div className="flex items-center">
                                                <MapPin size={16} className="mr-2" />
                                                Por Tiendas
                                            </div>
                                        </button>
                                    </div>

                                    <ExportMenu tickets={visibleTickets} title="Reporte_General_Tickets" />
                                </div>

                                {dashboardMode === 'STORES' ? (
                                    <StoreTicketGrid
                                        tickets={visibleTickets}
                                        onSelectStore={(siteId) => {
                                            const site = sites.find(s => s.id === siteId);
                                            if (site) {
                                                setSearchQuery(site.name);
                                                setDashboardMode('KPI');
                                                addNotification('info', `Filtrando por: ${site.name}`);
                                            }
                                        }}
                                    />
                                ) : (
                                    <>
                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                            <KPICard title="Por Asignar (Triage)" value={triageTickets.length} color="bg-purple-600" icon={Inbox} subtext="Requieren atención" />
                                            <KPICard title="En Gestión" value={activeTickets.length} color="bg-blue-600" icon={Activity} subtext="En manos de técnicos" />
                                            <KPICard title="Críticos (P1)" value={criticalTickets.length} color="bg-red-600" icon={Siren} subtext="Urgente" />
                                            <KPICard title="SLA Promedio" value="94%" color="bg-green-600" icon={CheckCircle2} />
                                        </div>

                                        {triageTickets.length > 0 && (
                                            <div className="border-2 border-purple-100 bg-purple-50 rounded-xl overflow-hidden">
                                                <div className="px-6 py-4 border-b border-purple-100 flex justify-between items-center bg-purple-100">
                                                    <div className="flex items-center text-purple-900">
                                                        <Inbox size={20} className="mr-2" />
                                                        <h3 className="font-bold">Bandeja de Entrada / Triage</h3>
                                                    </div>
                                                    <span className="bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded-full">{triageTickets.length} nuevos</span>
                                                </div>
                                                <div className="overflow-x-auto">
                                                    <table className="w-full text-left text-sm bg-white">
                                                        <thead className="bg-purple-50 text-purple-900 uppercase font-bold text-xs">
                                                            <tr>
                                                                <th className="px-6 py-2">Sitio</th>
                                                                <th className="px-6 py-2">Prioridad</th>
                                                                <th className="px-6 py-2">Problema</th>
                                                                <th className="px-6 py-2">SLA</th>
                                                                <th className="px-6 py-2">Acción</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-gray-100">
                                                            {triageTickets.map(t => {
                                                                const sla = getSLAStatus(t.slaDue);
                                                                return (
                                                                    <tr key={t.id} className="hover:bg-purple-50 transition">
                                                                        <td className="px-6 py-3 font-bold text-gray-800">{t.siteName}</td>
                                                                        <td className="px-6 py-3">
                                                                            <span className={`px-2 py-1 rounded text-xs font-bold ${getPriorityColor(t.priority)}`}>{t.priority}</span>
                                                                        </td>
                                                                        <td className="px-6 py-3">
                                                                            <div className="font-medium">{t.category}</div>
                                                                            <div className="text-xs text-gray-500">{t.subcategory}</div>
                                                                        </td>
                                                                        <td className="px-6 py-3">
                                                                            <span className={`text-xs font-medium ${getSLAColor(sla)}`}>{formatSLATime(t.slaDue)}</span>
                                                                        </td>
                                                                        <td className="px-6 py-3">
                                                                            <Button
                                                                                onClick={() => { setSelectedTicket(t); setView('TICKET_DETAIL'); }}
                                                                                size="sm"
                                                                                variant="primary"
                                                                            >
                                                                                Gestionar
                                                                            </Button>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}

                                        {activeTickets.length > 0 && (
                                            <div>
                                                <h3 className="font-bold text-gray-800 mb-4">Tickets en Curso</h3>
                                                <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                                                    <table className="w-full text-left text-sm">
                                                        <thead className="bg-gray-50 text-gray-500 uppercase font-bold text-xs">
                                                            <tr>
                                                                <th className="px-6 py-3">ID</th>
                                                                <th className="px-6 py-3">Sitio</th>
                                                                <th className="px-6 py-3">Asunto</th>
                                                                <th className="px-6 py-3">Estado</th>
                                                                <th className="px-6 py-3">Asignado a</th>
                                                                <th className="px-6 py-3"></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-gray-100">
                                                            {activeTickets.map(ticket => (
                                                                <tr
                                                                    key={ticket.id}
                                                                    className="hover:bg-gray-50 transition cursor-pointer"
                                                                    onClick={() => { setSelectedTicket(ticket); setView('TICKET_DETAIL'); }}
                                                                >
                                                                    <td className="px-6 py-4 font-medium text-gray-900">{ticket.id}</td>
                                                                    <td className="px-6 py-4 text-gray-700">{ticket.siteName}</td>
                                                                    <td className="px-6 py-4">
                                                                        <div className="font-medium text-gray-900">{ticket.category}</div>
                                                                    </td>
                                                                    <td className="px-6 py-4">
                                                                        <span className={`px-2 py-1 rounded text-xs font-bold border ${getStatusColor(ticket.status)}`}>
                                                                            {getStatusLabel(ticket.status)}
                                                                        </span>
                                                                    </td>
                                                                    <td className="px-6 py-4 text-xs">
                                                                        {ticket.assignedToName || <span className="text-gray-400">--</span>}
                                                                    </td>
                                                                    <td className="px-6 py-4 text-right">
                                                                        <ChevronRight size={16} className="text-gray-400" />
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}

                                        {visibleTickets.length === 0 && (
                                            <EmptyState
                                                icon={FileText}
                                                title="No hay tickets"
                                                description="No se encontraron tickets en el sistema"
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {view === 'DASHBOARD' && isStore && (
                            <div className="space-y-6">
                                <div className="bg-white p-6 rounded-xl border shadow-sm">
                                    <h2 className="text-xl font-bold mb-1">Mi Tienda: {currentUser.siteName}</h2>
                                    <p className="text-gray-500 text-sm mb-6">Estado actual de conectividad y servicios.</p>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <KPICard title="Mis Tickets Activos" value={visibleTickets.filter(t => t.status !== 'CLOSED').length} color="bg-blue-500" icon={FileText} />
                                        <KPICard title="Esperando mi respuesta" value={visibleTickets.filter(t => t.status === 'WAITING_INFO').length} color="bg-yellow-500" icon={Clock} />
                                        <KPICard title="Resueltos (Mes)" value={visibleTickets.filter(t => t.status === 'RESOLVED').length} color="bg-green-500" icon={CheckCircle2} />
                                    </div>
                                </div>

                                <div>
                                    <h3 className="font-bold text-gray-800 mb-4">Mis Solicitudes</h3>
                                    {visibleTickets.length === 0 ? (
                                        <EmptyState
                                            icon={FileText}
                                            title="No tienes tickets activos"
                                            description="Crea una nueva solicitud para comenzar"
                                        />
                                    ) : (
                                        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                                            <table className="w-full text-left text-sm">
                                                <thead className="bg-gray-50 text-gray-500 uppercase font-bold text-xs">
                                                    <tr>
                                                        <th className="px-6 py-3">ID</th>
                                                        <th className="px-6 py-3">Problema</th>
                                                        <th className="px-6 py-3">Estado</th>
                                                        <th className="px-6 py-3">Prioridad</th>
                                                        <th className="px-6 py-3">Actualizado</th>
                                                        <th className="px-6 py-3"></th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100">
                                                    {visibleTickets.map(ticket => (
                                                        <tr
                                                            key={ticket.id}
                                                            className="hover:bg-gray-50 transition cursor-pointer"
                                                            onClick={() => { setSelectedTicket(ticket); setView('TICKET_DETAIL'); }}
                                                        >
                                                            <td className="px-6 py-4 font-medium text-gray-900">{ticket.id}</td>
                                                            <td className="px-6 py-4">
                                                                <div className="font-medium text-gray-900">{ticket.category}</div>
                                                                <div className="text-gray-500 text-xs truncate max-w-xs">{ticket.description}</div>
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <span className={`px-2 py-1 rounded text-xs font-bold border ${getStatusColor(ticket.status)}`}>
                                                                    {getStatusLabel(ticket.status)}
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <span className={`px-2 py-1 rounded text-xs font-bold ${getPriorityColor(ticket.priority)}`}>{ticket.priority}</span>
                                                            </td>
                                                            <td className="px-6 py-4 text-gray-500 text-xs">{getRelativeTime(ticket.updatedAt)}</td>
                                                            <td className="px-6 py-4 text-right">
                                                                <ChevronRight size={16} className="text-gray-400" />
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}



                        {view === 'TICKET_DETAIL' && selectedTicket && (
                            <TicketDetail
                                ticket={selectedTicket}
                                user={currentUser}
                                users={users}
                                onBack={() => setView('DASHBOARD')}
                                onDelete={handleDeleteTicket}
                                onUpdate={handleUpdateTicket}
                                onArchive={handleArchiveTicket}
                            />
                        )}

                        {view === 'SITES' && !isStore && (
                            <SiteManagement
                                sites={sites}
                                onUpdateSite={handleUpdateSite}
                                readOnly={currentUser.role === 'TECH'}
                            />
                        )}

                        {view === 'USERS' && isAdmin && (
                            <UserManagement
                                users={users}
                                onUpdateUser={handleUpdateUser}
                                onDeleteUser={handleDeleteUser}
                                onCreateUser={handleCreateUser}
                            />
                        )}

                        {view === 'ASSETS' && !isStore && (
                            <AssetsInventory sites={sites} />
                        )}

                        {view === 'ANALYTICS' && isAdmin && (
                            <AnalyticsDashboard tickets={tickets} />
                        )}

                        {view === 'KNOWLEDGE' && (
                            <KnowledgeBase />
                        )}

                        {view === 'HISTORY' && (
                            <div className="space-y-6">
                                <h2 className="text-xl font-bold mb-4">Historial de Casos</h2>
                                <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                                    <table className="w-full text-left text-sm">
                                        <thead className="bg-gray-50 text-gray-500 uppercase font-bold text-xs">
                                            <tr>
                                                <th className="px-6 py-3">ID</th>
                                                <th className="px-6 py-3">Sitio</th>
                                                <th className="px-6 py-3">Asunto</th>
                                                <th className="px-6 py-3">Estado</th>
                                                <th className="px-6 py-3">Resolución</th>
                                                <th className="px-6 py-3">Responsable</th>
                                                <th className="px-6 py-3"></th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {historyTickets.map(ticket => (
                                                <tr
                                                    key={ticket.id}
                                                    className="hover:bg-gray-50 transition cursor-pointer"
                                                    onClick={() => { setSelectedTicket(ticket); setView('TICKET_DETAIL'); }}
                                                >
                                                    <td className="px-6 py-4 font-medium text-gray-900">{ticket.ticketNumber || ticket.id.substring(0, 8)}</td>
                                                    <td className="px-6 py-4 text-gray-700">{ticket.siteName}</td>
                                                    <td className="px-6 py-4">
                                                        <div className="font-medium text-gray-900">{ticket.category}</div>
                                                        <div className="text-xs text-gray-500">{ticket.subcategory}</div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className={`px-2 py-1 rounded text-xs font-bold border ${getStatusColor(ticket.status)}`}>
                                                            {getStatusLabel(ticket.status)}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-xs text-gray-500">
                                                        {getRelativeTime(ticket.updatedAt)}
                                                    </td>
                                                    <td className="px-6 py-4 text-xs">
                                                        {ticket.assignedToName || <span className="text-gray-400">--</span>}
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <ChevronRight size={16} className="text-gray-400" />
                                                    </td>
                                                </tr>
                                            ))}
                                            {historyTickets.length === 0 && (
                                                <tr>
                                                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                                                        <div className="flex flex-col items-center justify-center">
                                                            <Inbox size={48} className="mb-4 text-gray-300" />
                                                            <p>No tienes casos en el historial.</p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div >

            {showWizard && (
                <CreateTicketWizard
                    user={currentUser}
                    sites={sites}
                    onCancel={() => setShowWizard(false)}
                    onSubmit={handleCreateTicket}
                />
            )
            }
        </>
    );
};

const root = createRoot(document.getElementById('root')!);
root.render(
    <AppRouter MainApp={App} />
);
